<template>
  <div class="w-100" style="min-height:38px;text-align:left;">
    <b-form>
      <div class="form-container-row">
        <b-form-group class="form-container-element" label="Proteína (gr x kg)" label-for="input-prot-gr">
          <b-form-input 
            v-model="proteinPerKilo"
            id="input-prot-gr" 
            aria-describedby="input-prot-gr-help input-prot-gr-feedback"
            :state="$v.proteinPerKilo.$invalid ? false : null "
          />
          <b-form-invalid-feedback v-if="!$v.proteinPerKilo.decimal" id="input-prot-gr-feedback"> Debe ser un número </b-form-invalid-feedback>
          <b-form-invalid-feedback v-else id="input-prot-gr-feedback"> Campo obligatorio </b-form-invalid-feedback>
          <b-form-text id="input-prot-gr-help">Sugerido: {{suggestedProteinGrams}}</b-form-text>
        </b-form-group>
        <b-form-group class="form-container-element" label="Peso utilizado" label-for="input-weight-used">
          <b-form-select
            v-model="usedWeight" 
            :options="options.optionsWeight"
            id="input-weight-used" 
            aria-describedby="input-weight-used-help input-used-weight-feedback"
            :state="$v.usedWeight.$invalid ? false : null "
          />
          <b-form-invalid-feedback id="input-used-weight-feedback"> Campo obligatorio </b-form-invalid-feedback>
          <b-form-text id="input-weight-used-help">Sugerido: {{suggestedWeight}}</b-form-text>
        </b-form-group>
        <b-form-group class="form-container-element" label="Lípidos (%)" label-for="input-lipid-perc">
          <b-form-input 
            v-model="lipidPercentage" 
            id="input-lipid-perc" 
            aria-describedby="input-lipid-perc-help input-lipid-perc-feedback"
            :state="$v.lipidPercentage.$invalid ? false : null "
          />
          <b-form-invalid-feedback v-if="!$v.lipidPercentage.numeric" id="input-lipid-perc-feedback"> Debe ser un número </b-form-invalid-feedback>
          <b-form-invalid-feedback v-else id="input-lipid-perc-feedback"> Campo obligatorio </b-form-invalid-feedback>
          <b-form-text id="input-lipid-perc-help">Sugerido: {{suggestedLipidPercentage}} %</b-form-text>
        </b-form-group>
      </div>
      <MacronutrientsTable
        :calories="requirements"
        :protein-grams="proteinGrams"
        :lipid-grams="lipidGrams"
        :cho-grams="choGrams"
      />
    </b-form>
  </div>
</template>

<script>

import { decimal, minValue, numeric, required } from 'vuelidate/lib/validators'
import MacronutrientsTable from '../table/MacronutrientsTable'

export default {
  name: "MacronutrientsSettings",
  components: {
    MacronutrientsTable
  },
  model: {
    prop: "dietData",
    event: "change",
  },
  props: {
    dietData: {
      type: Object,
      default: function () {
        return {
          height: undefined,
          weight: undefined,
          objective: undefined,
          requirements: undefined,
          protein_grams: undefined,
          lipid_grams: undefined,
          cho_grams: undefined,
        }
      },
    }
  },
  data: function() {
    return {
      formData: this.dietData,
      lipidPercentage: 0,
      usedWeight: null,
      proteinPerKilo: 0,
      height: this.dietData.height,
      weight: this.dietData.weight,
      requirements: this.dietData.requirements,
      objective: this.dietData.objective,
      options: {
        optionsWeight: [
          { value: 0, text: "Peso real" },
          { value: 1, text: "Peso máximo aceptable" },
          { value: 2, text: "Peso ideal" }
        ],
        optionsObjective: [
          { value: 1, text: "Bajar de peso" },
          { value: 2, text: "Aumentar masa muscular" },
          { value: 5, text: "Mantener peso" }
        ] 
      }
    };
  },
  validations: {
    formData:{
      cho_grams: {
        required,
        numeric,
        minValue: minValue(0)
      },
      lipid_grams: {
        required,
        numeric,
        minValue: minValue(0)
      },
      protein_grams: {
        required,
        numeric,
        minValue: minValue(0)
      }
    },
    proteinPerKilo: {
      decimal,
      required,
      minValue: minValue(0.1)
    },
    usedWeight: {
      required
    },
    lipidPercentage: {
      numeric,
      required,
      minValue: minValue(1)
    }
  },
  computed: {
    maxWeight: function () {
      let height = this.height
      if (isNaN(height)) {
        return undefined
      } else {
        return ((height/100) * (height/100) * 24.9).toFixed(1)
      }
    },
    idealWeight: function () {
      let height = this.height
      if (isNaN(height)) {
        return undefined
      } else {
        return ((height/100) * (height/100) * 21.7).toFixed(1)
      }
    },
    proteinGrams: function () {
      let proteinPerKilo = this.proteinPerKilo
      let weightIndex = this.usedWeight
      let weights = [this.weight, this.maxWeight, this.idealWeight]
      if (isNaN(proteinPerKilo) || isNaN(weightIndex)) {
        return undefined
      } else {
        let weight = weights[weightIndex]
        if (isNaN(weight)) {
          return undefined
        }
        return (proteinPerKilo * weight).toFixed(0)
      }
    },
    lipidGrams: function () {
      let percentage = this.lipidPercentage
      let calories = this.requirements
      if (isNaN(percentage) || isNaN(calories)) {
        return undefined
      } else {
        let lipidCalories = ((percentage * calories) / 100)
        return (lipidCalories / 9).toFixed(0)
      }
    },
    choGrams: function () {
      let protein = this.proteinGrams
      let lipids = this.lipidGrams
      let calories = this.requirements
      if (isNaN(protein) || isNaN(lipids) || isNaN(calories)) {
        return undefined
      } else {
        let proteinCalories = protein * 4
        let lipidCalories = lipids * 9
        let choCalories = calories - proteinCalories - lipidCalories
        return (choCalories / 4).toFixed(0)
      }
    },
    suggestedProteinGrams: function () {
      let objective = this.objective
      if ( objective === 1 ) {
        return 2.2
      } else if ( objective === 2 ) {
        return 2
      } else if ( objective === 5 ) {
        return 2
      } else {
        return "--"
      }
    },
    suggestedWeight: function () {
      let objective = this.objective
      if ( objective === 1 ) {
        return "Peso máximo aceptable"
      } else if ( objective === 2 ) {
        return "Peso real"
      } else if ( objective === 5 ) {
        return "Peso máximo aceptable"
      } else {
        return "--"
      }
    },
    suggestedLipidPercentage: function () {
      let objective = this.objective
      if ( objective === 1 ) {
        return 25
      } else if ( objective === 2 ) {
        return 30
      } else if ( objective === 5 ) {
        return 25
      } else {
        return "--"
      }
    },
  },
  watch: {
    formData: function(val) {
      this.$emit("change", val)
    },
    maxWeight: {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.options.optionsWeight[1].text = "Peso máximo aceptable - " + value + " kg"
        }
      }
    },
    idealWeight: {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.options.optionsWeight[2].text = "Peso ideal - " + value + " kg"
        }
      }
    },
    "dietData.weight": {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.weight = value
          this.options.optionsWeight[0].text = "Peso real - " + value + " kg"
        }
      }
    },
    "dietData.height": {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.height = value
        }
      }
    },
    "dietData.requirements": {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.requirements = value
        }
      }
    },
    "dietData.objective": {
      immediate: true,
      handler: function (objective) {
        this.objective = objective
        if ( objective === 1 ) {
          this.proteinPerKilo = 2.2
          this.usedWeight = 1
          this.lipidPercentage = 25
        } else if ( objective === 2 ) {
          this.proteinPerKilo = 2
          this.usedWeight = 0
          this.lipidPercentage = 30
        } else if ( objective === 5 ) {
          this.proteinPerKilo = 2
          this.usedWeight = 1
          this.lipidPercentage = 25
        } 
      }
    },
    proteinGrams: {
      immediate: true,
      handler: function (value) {
        this.formData.protein_grams = value
      }
    },
    lipidGrams: {
      immediate: true,
      handler: function (value) {
        this.formData.lipid_grams = value
      }
    },
    choGrams: {
      immediate: true,
      handler: function (value) {
        this.formData.cho_grams = value
      }
    },
  },
  methods: {
    validate: function() {
      this.$v.$touch()
      var isValid = !this.$v.$invalid
      this.$emit('on-validate', isValid)
      return isValid
    }
  }
};
</script>

<style>
  .form-container-row {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  }
  .form-container-element {
    flex: 1 1 auto;
    padding: 0.5rem;
  }
</style>